<script setup lang="ts">
import type { ExtendedBlogPostProps } from '~/types/elementsUi'
import dayjs from 'dayjs'
import { smartSplit } from 'nano-string-utils'
import type { BlogPostProps } from '@nuxt/ui'
import { nuxtUiMerge } from '~/utils/tw-utils'
import { getImageSource, localizeDate } from '~/utils/helpers'
import { omit } from 'es-toolkit'
import { toValue } from 'vue'

interface VBlogPostsProps {
  blogPosts: ExtendedBlogPostProps[]
  ui?: BlogPostProps['ui']
}

const props = defineProps<VBlogPostsProps>()

const localeStore = useLocaleStore()
const locale = computed(() => localeStore.locale)

const replaceRegex = /[.!?]+$/
const parseDescription = (description?: string) => {
  if (!description) {
    return undefined
  }

  const splitSentences = smartSplit(description)

  if (splitSentences.length === 1) {
    return description
  }

  const firstSentence = splitSentences[0]
  return firstSentence.replace(replaceRegex, '...')
}

const computedUI = computed<BlogPostProps['ui']>(() =>
  nuxtUiMerge(props.ui, {
    root: tw`bg-accented`,
    date: tw`text-default`,
    description: tw`text-default`,
    meta: tw`flex-wrap`,
    title: tw`paragraph-title mt-0`
  })
)
</script>

<template>
  <UBlogPosts>
    <UBlogPost
      v-for="(blogPost, blogPostIndex) in blogPosts"
      :key="`blog-post-${blogPostIndex}`"
      v-bind="{
        ...omit(blogPost, ['badges']),
        description: parseDescription(blogPost.description),
        image: {
          src: getImageSource(blogPost.image.src),
          alt: blogPost.image.alt[locale]
        }
      }"
      :ui="computedUI"
    >
      <template #title>
        {{ blogPost.title }}
      </template>
      <template #date>
        <span v-if="!toValue(blogPost.hideDate)">
          {{ localizeDate(dayjs(blogPost.date), 'll') }}
        </span>
        <span v-else>&nbsp;</span>
      </template>
      <template #badge>
        <VTag
          v-for="(badge, badgeIndex) in blogPost.badges"
          :key="`badge-${badgeIndex}`"
          size="small"
          :color="badge.color"
          :icon-left="badge.icon"
          :label="badge.title"
        />
      </template>
    </UBlogPost>
  </UBlogPosts>
</template>

<style scoped></style>
